import { Ref, useRef } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';

import { DocViewerFact } from './doc-fact';
import { Icon } from '../icon';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Button } from '@/new/components/shadcn/button';
import { Fact } from '@/types';

interface DocViewerFactSidebarProps {
  docId: string;
  factTotal: number | null;
  isLoading: boolean;
  jumpToHighlightArea: (id: string) => void;
  highlightedFactId: string | null;
  selectFact: (fact: Fact) => void;
  facts: Fact[];
  listRef: Ref<List>;
  setFactAdderModalIsOpen: (bool: boolean) => void;
}

export const DocViewerFactSidebar = ({
  docId,
  factTotal,
  jumpToHighlightArea,
  highlightedFactId,
  selectFact,
  facts,
  listRef,
  isLoading,
  setFactAdderModalIsOpen,
}: DocViewerFactSidebarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { subsetId, matterId } = useParams();

  const handleAskKim = () => {
    trackCustomEvent(TrackingEvents.AskKimFromDocViewer, { docId });
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('selectedDocId', docId);
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim?${searchParams.toString()}`);
  };

  const Row = ({ index, style }: ListChildComponentProps) => {
    const fact = facts[index];
    if (!fact) return null;

    return (
      <div style={style} className="flex items-center gap-2 py-2 pl-0.5">
        <Button size="icon" onClick={() => jumpToHighlightArea(fact.id)} className="rounded-full border bg-gray-100">
          <Icon className="lucide--search h-3 w-3" />
        </Button>
        <div
          id={`fact-${fact.id}`}
          className={`flex-grow cursor-pointer rounded-md border bg-white shadow-sm transition-all duration-300 ease-in-out hover:bg-gray-100 ${
            highlightedFactId === fact.id ? 'border-brandSecondary' : ''
          }`}
          onClick={() => {
            trackCustomEvent(TrackingEvents.JumpToFactInDocFromDocFactsList, { docId });
            selectFact(fact);
            jumpToHighlightArea(fact.id);
          }}
        >
          {<DocViewerFact fact={fact} />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-5/12 flex-col border-l bg-gray-50">
      <div className="flex h-14 w-full items-center justify-between border-b bg-white pl-4 font-medium">
        <div className="flex items-center gap-2">
          <Icon className="lucide--list-check" />
          Facts <span className="text-sm text-gray-500">{factTotal ? `(${factTotal})` : ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleAskKim} className="text-xs">
            <Icon className="lucide--file-question" />
            Ask Kim
          </Button>
          <Button onClick={() => setFactAdderModalIsOpen(true)} className="text-xs">
            <Icon className="lucide--plus-circle" />
            Add Fact
          </Button>
        </div>
      </div>
      <div id="document-facts-container" className="flex-1 overflow-y-auto" ref={containerRef}>
        <div className="flex h-full flex-col gap-2 bg-gray-50 p-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Icon className="lucide--loader-circle animate-spin" />
            </div>
          ) : facts.length > 0 ? (
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => (
                <List ref={listRef} height={height} itemCount={facts.length} itemSize={72} width={width}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <Icon className="lucide--question-circle" />
              <span className="text-sm text-gray-500">Could not find any facts in this document.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
